import {
  StockTransferStatus,
  ProductionOrderStatus,
  BranchRequisitionStatus,
} from './centralKitchenApi';

/**
 * Status tones, kept LOCAL rather than added to `@/components/ui/badge`'s
 * shared table — matching the precedent `Pages/Integrations/lib.ts` and
 * `Pages/Fiscal/lib.ts` set. `IN_TRANSIT` is the one that matters most here:
 * ADR-0015 §3 exists specifically to make stock on the road a real, counted
 * state rather than a gap between two instantaneous movements, so it gets its
 * own tone rather than borrowing `QUEUED`'s.
 */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const TRANSFER_TONES: Record<StockTransferStatus, Tone> = {
  DRAFT: 'neutral',
  // On the road, counted at neither end — the state this whole epic exists to
  // make visible. `info`, not `warning`: an in-transit transfer is not a
  // problem, it is the ordinary middle of a journey.
  IN_TRANSIT: 'info',
  RECEIVED: 'success',
  // A discrepancy is a deliberate, loud state — the ledger is saying five
  // units are unaccounted for, out loud, rather than absorbing them.
  DISCREPANT: 'warning',
  RECONCILED: 'success',
  CANCELLED: 'neutral',
};

export function transferStatusTone(status: StockTransferStatus | string): Tone {
  return TRANSFER_TONES[status as StockTransferStatus] ?? 'neutral';
}

const PRODUCTION_TONES: Record<ProductionOrderStatus, Tone> = {
  DRAFT: 'neutral',
  RELEASED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

export function productionStatusTone(status: ProductionOrderStatus | string): Tone {
  return PRODUCTION_TONES[status as ProductionOrderStatus] ?? 'neutral';
}

const REQUISITION_TONES: Record<BranchRequisitionStatus, Tone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
  FULFILLED: 'success',
  CANCELLED: 'neutral',
};

export function requisitionStatusTone(status: BranchRequisitionStatus | string): Tone {
  return REQUISITION_TONES[status as BranchRequisitionStatus] ?? 'neutral';
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatQuantity(value: string | null | undefined, locale: string): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    maximumFractionDigits: 3,
  }).format(n);
}
